import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';

const TOGGLE_ARCHIVE_CONVERSATION = gql`
  mutation toggleArchiveConversation($id: ID!) {
    toggleArchiveConversation(id: $id)
  }
`;

const ArchiveButton = ({ 
  currentUser, 
  conversationId, 
  conversation, 
  onConversationUpdate 
}) => {
  const intl = useIntl();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [toggleArchiveConversation] = useMutation(TOGGLE_ARCHIVE_CONVERSATION, {
    refetchQueries: ['GetPersonalConversations', 'GetConversation'],
    awaitRefetchQueries: true,
    // Also update the cache to ensure immediate UI updates
    update: (cache) => {
      // Evict all conversation queries from cache to force refetch
      cache.evict({ fieldName: 'getPersonalConversations' });
      cache.evict({ fieldName: 'getConversation' });
      cache.gc();
    }
  });

  // Determine if the current user has archived the conversation
  const currentUserIsParticipant1 = String(conversation?.participant1?.id) === String(currentUser?.id);
  const isArchivedByCurrentUser = currentUserIsParticipant1 
    ? conversation?.archivedByParticipant1 
    : conversation?.archivedByParticipant2;


  const handleToggleArchive = async () => {
    if (onConversationUpdate) {
      const updatedConversation = {
        ...conversation,
        archivedByParticipant1: currentUserIsParticipant1
          ? !isArchivedByCurrentUser
          : conversation?.archivedByParticipant1,
        archivedByParticipant2: !currentUserIsParticipant1
          ? !isArchivedByCurrentUser
          : conversation?.archivedByParticipant2
      };
      onConversationUpdate(updatedConversation);
    }

    setIsMenuOpen(false);

    try {
      await toggleArchiveConversation({
        variables: { id: conversationId }
      });
    } catch (error) {
      console.error('📦 ArchiveButton: Error toggling archive status:', error);
      if (onConversationUpdate) {
        onConversationUpdate(conversation);
      }
      alert(`Error archiving conversation: ${error.message}`);
    }
  };
  
  return (
    <div className="relative">
      {/* Menu Button */}
      <button
        onClick={() => {
          setIsMenuOpen(!isMenuOpen);
        }}
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          border: isArchivedByCurrentUser
            ? '2px solid var(--succession-primary)'
            : '2px solid var(--succession-border)',
          backgroundColor: isArchivedByCurrentUser
            ? 'var(--succession-primary-light, rgba(74, 144, 226, 0.1))'
            : 'var(--succession-bg-secondary)',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          outline: 'none'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'var(--succession-bg-primary)';
          e.target.style.borderColor = 'var(--succession-primary)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = isArchivedByCurrentUser
            ? 'var(--succession-primary-light, rgba(74, 144, 226, 0.1))'
            : 'var(--succession-bg-secondary)';
          e.target.style.borderColor = isArchivedByCurrentUser
            ? 'var(--succession-primary)'
            : 'var(--succession-border)';
        }}
        onFocus={(e) => {
          e.target.style.boxShadow = '0 0 0 2px var(--succession-primary-light)';
        }}
        onBlur={(e) => {
          e.target.style.boxShadow = 'none';
        }}
        title={isArchivedByCurrentUser ? intl.formatMessage({ id: 'succession.messaging.archive_button.unarchive_title' }) : intl.formatMessage({ id: 'succession.messaging.archive_button.archive_title' })}
      >
        <svg 
          style={{ width: '20px', height: '20px', color: isArchivedByCurrentUser ? 'var(--succession-primary)' : 'var(--succession-text-secondary)' }}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          {isArchivedByCurrentUser ? (
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" 
            />
          ) : (
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" 
            />
          )}
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <>
          {/* Overlay to close menu when clicking outside */}
          <div 
            style={{ 
              position: 'fixed',
              inset: 0,
              zIndex: 40
            }}
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Menu */}
          <div style={{
            position: 'absolute',
            right: '8px',
            top: 0,
            zIndex: 50,
            width: '192px',
            backgroundColor: 'var(--succession-bg-primary)',
            borderRadius: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            border: '1px solid var(--succession-border)',
            paddingTop: '8px',
            paddingBottom: '8px'
          }}>
            <button
              onClick={() => {
                handleToggleArchive();
              }}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: '14px',
                color: 'var(--succession-text-primary)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--succession-bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <svg 
                style={{ width: '16px', height: '16px' }}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                {isArchivedByCurrentUser ? (
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" 
                  />
                ) : (
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" 
                  />
                )}
              </svg>
              {isArchivedByCurrentUser ? intl.formatMessage({ id: 'succession.messaging.archive_button.unarchive_chat' }) : intl.formatMessage({ id: 'succession.messaging.archive_button.archive_chat' })}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ArchiveButton;

